// 2024 Epic Boons (SRD 5.2.1, level 19+ feats) with automated benefits. Each boon's +1 ability increase is handled by
// feat selection; this module holds the rules for the benefits the engines apply:
// - Combat Prowess · Peerless Aim: when you miss with an attack roll, you can hit instead; then not again until the
//   start of your next turn. Automated on any turn (opportunity attacks too); the server keeps the claim until the boon
//   holder's next turn starts.
// - Irresistible Offense · Overcome Defenses: your Bludgeoning, Piercing and Slashing damage ignores Resistance.
//   Overwhelming Strike: on a 20 on the d20 of an attack roll, extra damage equal to the increased ability score,
//   of the attack's damage type.
// - Spell Recall · Free Casting: casting with a level 1-4 slot rolls 1d4; on the slot's level the slot isn't expended.
// - Fate · Improve Fate: after a D20 Test, roll 2d4 and add it to (or subtract it from) the d20 roll; again after
//   rolling Initiative or a Short or Long Rest. Automated on the boon holder's own d20 tests (as a bonus);
//   other creatures' rolls are adjudicated at the table for now.
// - Dimensional Travel · Blink Steps: right after the Attack action or the Magic action, teleport up to 30 feet (one
//   teleport per such action, on the server's turn ledger).
// - the Night Spirit: while the player marks the character as in Dim Light or Darkness, Shadowy Form gives Resistance to
//   all damage but Psychic and Radiant, and Merge with Shadows (a Bonus Action) gives Invisible until the character
//   takes an action, a Bonus Action or a Reaction.
// - Truesight: a reminder; the DM marks Truesight viewers on an Invisible creature.
const featKey = (feat) => String(feat?.featName ?? feat ?? '').trim().toLowerCase();
const boonEntry = (character, name) => (Array.isArray(character?.feat) ? character.feat : []).find((feat) => featKey(feat) === name) || null;

export const COMBAT_PROWESS = 'boon of combat prowess';
export const IRRESISTIBLE_OFFENSE = 'boon of irresistible offense';
export const SPELL_RECALL = 'boon of spell recall';
export const FATE = 'boon of fate';
export const DIMENSIONAL_TRAVEL = 'boon of dimensional travel';
export const NIGHT_SPIRIT = 'boon of the night spirit';
export const TRUESIGHT = 'boon of truesight';
export const hasEpicBoon = (character, name) => Boolean(boonEntry(character, name));

export const PEERLESS_AIM_FEATURE_ID = 'feat:boon-of-combat-prowess';
/** Peerless Aim is offered in combat on any turn until it is used; it is usable again from the holder's next turn. */
export const canUsePeerlessAim = (character, combat, combatantId) => {
  if (!hasEpicBoon(character, COMBAT_PROWESS) || !Number.isInteger(combat?.turnSequence) || !Number.isInteger(combat?.activeTurn)) return false;
  return !combat.turnFeatureUsage?.[String(combatantId)]?.[PEERLESS_AIM_FEATURE_ID];
};
export const peerlessAimDecision = (resolutionId) => ({
  id: `${resolutionId}:${PEERLESS_AIM_FEATURE_ID}`, featureId: PEERLESS_AIM_FEATURE_ID, title: 'Peerless Aim',
  prompt: 'Boon of Combat Prowess: you missed. Hit instead? You can’t use this again until the start of your next turn.',
  choices: [{ id: 'use', label: 'Hit instead', action: 'apply' }, { id: 'skip', label: 'Keep the miss' }],
});

const PHYSICAL = new Set(['bludgeoning', 'piercing', 'slashing']);
/** Overcome Defenses: the source's Bludgeoning, Piercing and Slashing damage ignores Resistance. */
export const ignoresResistance = (source, type) => PHYSICAL.has(String(type || '').trim().toLowerCase()) && hasEpicBoon(source, IRRESISTIBLE_OFFENSE);
export const isPhysicalDamageType = (type) => PHYSICAL.has(String(type || '').trim().toLowerCase());

/** The ability (str or dex) Irresistible Offense increased, or null. */
export const overwhelmingStrikeAbility = (character) => {
  const boon = boonEntry(character, IRRESISTIBLE_OFFENSE);
  if (!boon) return null;
  return ['str', 'dex'].find((ability) => Number(boon[ability]) > 0) || null;
};

/** Free Casting applies to level 1-4 slots: it saves the slot when 1d4 equals the slot level. */
/** What the caster is told after the d4; `slotKept` means it rolled the slot's level. */
export const spellRecallNotice = ({ roll, slotLevel, slotKept }) => (slotKept
  ? `Boon of Spell Recall: rolled ${roll} on the d4. The level ${slotLevel} spell slot isn’t expended.`
  : `Boon of Spell Recall: rolled ${roll} on the d4, needing a ${slotLevel}. The level ${slotLevel} spell slot is spent.`);
export const spellRecallApplies = (character, slotLevel) => hasEpicBoon(character, SPELL_RECALL) && Number.isInteger(slotLevel) && slotLevel >= 1 && slotLevel <= 4;

/**
 * Improve Fate's state on the character: `boonOfFate = { used, revision, requestId?, kind?, rolls? }`. Usable while not
 * used; rolling Initiative or a Short or Long Rest restores it.
 */
export const FATE_FEATURE_ID = 'feat:boon-of-fate';
export const canUseFate = (character) => hasEpicBoon(character, FATE) && character?.boonOfFate?.used !== true;
/** The restored state (null when there is nothing to restore). */
export const restoredFate = (character) => (hasEpicBoon(character, FATE) && character?.boonOfFate?.used === true
  ? { used: false, revision: (Number(character.boonOfFate.revision) || 0) + 1 } : null);
export const fateBonus = (rolls) => (Array.isArray(rolls) ? rolls.reduce((sum, die) => sum + die, 0) : 0);
export const fateDecision = (resolutionId, what) => ({
  id: `${resolutionId}:${FATE_FEATURE_ID}`, featureId: FATE_FEATURE_ID, title: 'Boon of Fate',
  prompt: `Your ${what} missed. Roll 2d4 and add it to the roll (Improve Fate)? It returns when you roll Initiative or finish a rest.`,
  choices: [{ id: 'use', label: 'Add 2d4', action: 'apply' }, { id: 'skip', label: 'Keep the roll' }],
});

/** Attack and Magic actions taken this turn, from the turn ledger (Action Surge can make two). */
const actionsTaken = ledger => (Array.isArray(ledger?.completedAttackActions) ? ledger.completedAttackActions.length : 0)
  + (ledger?.attackAction?.id ? 1 : 0) + Math.max(0, Number(ledger?.magicActions) || 0);
/** Blink Steps left this turn: one per Attack or Magic action taken, less those already used. */
export const blinkStepsRemaining = (character, ledger) => (hasEpicBoon(character, DIMENSIONAL_TRAVEL)
  ? Math.max(0, actionsTaken(ledger) - Math.max(0, Number(ledger?.blinkSteps) || 0)) : 0);
export const BLINK_STEPS_FEET = 30;

/** The Night Spirit's "in Dim Light or Darkness" mark (the map has no lighting, so the player or DM sets it). */
export const NIGHT_SPIRIT_SHADOW = 'boon:night-spirit-shadow';
export const NIGHT_SPIRIT_MERGE = 'boon:night-spirit';
export const inShadow = (combat, id) => (combat?.activeEffects || []).some(effect => effect?.definitionId === NIGHT_SPIRIT_SHADOW
  && String(effect.targetCombatantId) === String(id));
/** Shadowy Form: Resistance to all damage except Psychic and Radiant while in shadow. */
export const shadowyFormResists = (combat, id, type) => Boolean(type) && !['psychic', 'radiant'].includes(String(type).toLowerCase())
  && inShadow(combat, id);

/**
 * Merge with Shadows ends immediately after the creature takes an action, a Bonus Action or a Reaction. Read from the
 * turn ledger (the merge itself spent that turn's Bonus Action) and the creature's spent reactions.
 */
export function nightSpiritMergeEnded(effect, combat) {
  if (effect?.sourceFeature !== NIGHT_SPIRIT_MERGE) return false;
  const id = String(effect.targetCombatantId);
  if (!effect.reactionSpentAtMerge && (combat?.reactionStates || []).some(state => String(state?.combatantId) === id && state?.spent === true)) return true;
  const ledger = combat?.turnActionLedger;
  if (!ledger || String(ledger.combatantId) !== id || !Number.isInteger(ledger.turnSequence)) return false;
  const acted = ledger.actionSpent === true || Boolean(ledger.attackAction?.id) || ledger.actionSurge?.spent === true || Number(ledger.magicActions) > 0;
  if (ledger.turnSequence === effect.mergedAtTurnSequence) return acted;
  return ledger.turnSequence > effect.mergedAtTurnSequence && (acted || ledger.bonusActionSpent === true);
}
